import React, { useState, useRef } from 'react';
import { Camera, X, Loader2, Image } from 'lucide-react';

const ReviewPhotoUpload = ({ onPhotosChange, maxPhotos = 5, existingPhotos = [] }) => {
    const [photos, setPhotos] = useState(existingPhotos);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileSelect = async (event) => {
        const files = Array.from(event.target.files);

        if (photos.length + files.length > maxPhotos) {
            alert(`Maximum ${maxPhotos} photos allowed per review`);
            return;
        }

        setUploading(true);

        try {
            const newPhotos = [];

            for (const file of files) {
                // Validate file
                if (!file.type.startsWith('image/')) {
                    alert(`${file.name} is not an image file`);
                    continue;
                }

                if (file.size > 5 * 1024 * 1024) { // 5MB limit
                    alert(`${file.name} is too large. Maximum 5MB per photo.`);
                    continue;
                }

                // Create preview URL
                const previewUrl = URL.createObjectURL(file);

                const photoData = {
                    file: file,
                    preview: previewUrl,
                    caption: '',
                    photo_type: 'campsite',
                    temp_id: Date.now() + Math.random() // Temporary ID for tracking
                };

                newPhotos.push(photoData);
            }

            const updatedPhotos = [...photos, ...newPhotos];
            setPhotos(updatedPhotos);
            onPhotosChange(updatedPhotos);

        } catch (error) {
            console.error('Error processing photos:', error);
            alert('Error processing photos');
        } finally {
            setUploading(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const removePhoto = (tempId) => {
        const updatedPhotos = photos.filter(photo => photo.temp_id !== tempId);
        setPhotos(updatedPhotos);
        onPhotosChange(updatedPhotos);
    };

    const updatePhotoCaption = (tempId, caption) => {
        const updatedPhotos = photos.map(photo =>
            photo.temp_id === tempId ? { ...photo, caption } : photo
        );
        setPhotos(updatedPhotos);
        onPhotosChange(updatedPhotos);
    };

    const updatePhotoType = (tempId, photo_type) => {
        const updatedPhotos = photos.map(photo =>
            photo.temp_id === tempId ? { ...photo, photo_type } : photo
        );
        setPhotos(updatedPhotos);
        onPhotosChange(updatedPhotos);
    };

    return (
        <div className="space-y-4">
            {/* Upload Button */}
            <div className="flex items-center justify-between">
                <h6 className="text-sm font-medium text-gray-700">Add Photos to Review</h6>
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || photos.length >= maxPhotos}
                    className="flex items-center px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {uploading ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                        <Camera className="h-4 w-4 mr-1" />
                    )}
                    {uploading ? 'Processing...' : `Add Photos (${photos.length}/${maxPhotos})`}
                </button>
            </div>

            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*"
                multiple
                className="hidden"
            />

            {/* Photo Preview Grid */}
            {photos.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {photos.map((photo) => (
                        <div key={photo.temp_id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                            <div className="relative mb-2">
                                <img
                                    src={photo.preview}
                                    alt="Review location preview"
                                    className="w-full h-32 object-cover rounded-md"
                                />
                                <button
                                    type="button"
                                    onClick={() => removePhoto(photo.temp_id)}
                                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            </div>

                            <div className="space-y-2">
                                <div>
                                    <label className="block text-xs font-medium mb-1">Photo Type</label>
                                    <select
                                        value={photo.photo_type}
                                        onChange={(e) => updatePhotoType(photo.temp_id, e.target.value)}
                                        className="w-full p-1 border border-gray-300 rounded text-xs"
                                    >
                                        <option value="campsite">Campsite</option>
                                        <option value="view">View/Scenery</option>
                                        <option value="amenity">Amenity/Facility</option>
                                        <option value="road_access">Road/Access</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs font-medium mb-1">Caption (optional)</label>
                                    <input
                                        type="text"
                                        value={photo.caption}
                                        onChange={(e) => updatePhotoCaption(photo.temp_id, e.target.value)}
                                        placeholder="Describe this photo..."
                                        className="w-full p-1 border border-gray-300 rounded text-xs"
                                        maxLength="200"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {photos.length === 0 && (
                <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg">
                    <Image className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm text-gray-500">No photos added yet</p>
                    <p className="text-xs text-gray-400">Click "Add Photos" to include images with your review</p>
                </div>
            )}
        </div>
    );
};

export default ReviewPhotoUpload;